import { hkdf } from '@noble/hashes/hkdf'
import { hmac } from '@noble/hashes/hmac'
import { sha256 } from '@noble/hashes/sha256'
import { utf8ToBytes } from '@noble/hashes/utils'

// HKDF Info 的固定协议前缀，必须与后端 WalletSignKit 保持一致，不从 JWT 动态读取。
const HKDF_INFO_PREFIX = 'api_sign_v1'
const LEGACY_TOKEN_PREFIX_LENGTH = 7

let cachedToken = ''
let cachedSignKey = null

function hasText (value) {
  return typeof value === 'string' && value.trim().length > 0
}

function getRandomCrypto (cryptoImpl) {
  const runtimeCrypto = typeof globalThis !== 'undefined'
    ? globalThis.crypto
    : typeof window !== 'undefined' ? window.crypto : null
  const resolved = cryptoImpl || runtimeCrypto
  if (!resolved || typeof resolved.getRandomValues !== 'function') {
    throw new Error('当前浏览器不支持安全随机数，无法生成请求签名')
  }
  return resolved
}

function byteToHex (byte) {
  return `0${byte.toString(16)}`.slice(-2)
}

function bytesToHex (value) {
  return Array.from(value).map(byteToHex).join('')
}

function decodeBase64 (value) {
  if (typeof atob !== 'function') throw new Error('当前 WebView 不支持 Base64 解码')
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  let padded = normalized
  while (padded.length % 4) padded += '='
  const decoded = atob(padded)
  return Uint8Array.from(decoded, char => char.charCodeAt(0))
}

function decodeJwtJson (value) {
  const bytes = decodeBase64(value)
  let encoded = ''
  bytes.forEach(byte => { encoded += `%${byteToHex(byte)}` })
  return JSON.parse(decodeURIComponent(encoded))
}

function isJwt (value) {
  const parts = value.split('.')
  if (parts.length !== 3 || parts.some(part => !part)) return false
  try {
    return Boolean(decodeJwtJson(parts[0]) && decodeJwtJson(parts[1]))
  } catch (error) {
    return false
  }
}

export function normalizeJwtToken (tokenValue) {
  if (typeof tokenValue !== 'string' || !tokenValue.trim()) throw new Error('缺少 AccessToken')
  const token = tokenValue.trim().replace(/^Bearer\s+/i, '')
  const candidates = [token]
  if (token.length > LEGACY_TOKEN_PREFIX_LENGTH) {
    candidates.push(token.slice(LEGACY_TOKEN_PREFIX_LENGTH).replace(/^Bearer\s+/i, ''))
  }
  const jwt = candidates.find(isJwt)
  if (!jwt) throw new Error('AccessToken 不是有效的 JWT')
  return jwt
}

export function parseJwtPayload (accessToken) {
  const token = normalizeJwtToken(accessToken)
  return decodeJwtJson(token.split('.')[1])
}

export function minifyJsonBody (body) {
  if (body === undefined || body === null || body === '') return ''
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    throw new Error('暂不支持 multipart/form-data 请求签名')
  }
  if (typeof body === 'string') return JSON.stringify(JSON.parse(body))
  const serialized = JSON.stringify(body)
  if (serialized === undefined) throw new Error('请求体无法序列化为 JSON')
  return serialized
}

export async function sha256Hex (content) {
  return bytesToHex(sha256(utf8ToBytes(content)))
}

export async function generateBodySignContent (body) {
  const minifiedBody = minifyJsonBody(body)
  return { minifiedBody, bodyHash: await sha256Hex(minifiedBody) }
}

export function buildSignMetadata ({ method, endpointUrl, queryString, bodyHash, timestamp, nonce }) {
  if (!hasText(method)) throw new Error('签名请求缺少 HTTP 方法')
  if (!hasText(endpointUrl)) throw new Error('签名请求缺少 endpoint URL')
  if (typeof queryString !== 'string') throw new Error('签名请求 queryString 必须是字符串')
  if (!/^[0-9a-f]{64}$/.test(bodyHash)) throw new Error('签名请求 bodyHash 格式无效')
  if (!/^\d{10}$/.test(String(timestamp))) throw new Error('签名请求 timestamp 必须是10位秒级时间戳')
  if (!hasText(nonce)) throw new Error('签名请求缺少 nonce')
  return [String(method).toUpperCase(), endpointUrl, queryString, bodyHash, String(timestamp), nonce].join(':')
}

/** 使用成熟密码库按 HKDF-SHA256 派生 32 字节 HMAC 会话密钥。 */
export async function deriveSignKey (accessToken) {
  const token = normalizeJwtToken(accessToken)
  const payload = parseJwtPayload(token)
  const isolationFactor = hasText(payload.web_seed)
    ? payload.web_seed
    : hasText(payload.did) ? payload.did : ''
  if (!hasText(payload.salt)) throw new Error('AccessToken 缺少有效 salt')
  if (!isolationFactor) throw new Error('AccessToken 缺少 did 或 web_seed')
  return hkdf(
    sha256,
    utf8ToBytes(token),
    decodeBase64(payload.salt),
    utf8ToBytes(`${HKDF_INFO_PREFIX}:${isolationFactor}`),
    32
  )
}

async function getCachedSignKey (accessToken) {
  const token = normalizeJwtToken(accessToken)
  if (cachedToken !== token || !cachedSignKey) {
    cachedToken = token
    cachedSignKey = await deriveSignKey(token)
  }
  return cachedSignKey
}

export function clearSessionSignKey () {
  if (cachedSignKey) cachedSignKey.fill(0)
  cachedToken = ''
  cachedSignKey = null
}

/** 生成 UUID v4 nonce；randomUUID 不存在时使用安全随机数兼容。 */
export function createNonce (cryptoImpl) {
  const crypto = getRandomCrypto(cryptoImpl)
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0F) | 0x40
  bytes[8] = (bytes[8] & 0x3F) | 0x80
  const hex = bytesToHex(bytes)
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export async function hmacSha256Hex (content, signKey) {
  return bytesToHex(hmac(sha256, signKey, utf8ToBytes(content)))
}

export async function createRequestSignature ({
  accessToken,
  method,
  endpointUrl,
  queryString,
  body,
  timestamp,
  nonce
}, options = {}) {
  const token = normalizeJwtToken(accessToken)
  const requestTimestamp = timestamp === undefined
    ? Math.floor((options.now ? options.now() : Date.now()) / 1000)
    : timestamp
  const requestNonce = nonce === undefined ? createNonce(options.crypto) : nonce
  const { minifiedBody, bodyHash } = await generateBodySignContent(body)
  const metadata = buildSignMetadata({
    method,
    endpointUrl,
    queryString,
    bodyHash,
    timestamp: requestTimestamp,
    nonce: requestNonce
  })
  const signKey = options.cacheKey === false
    ? await deriveSignKey(token)
    : await getCachedSignKey(token)
  const sign = await hmacSha256Hex(metadata, signKey)
  return {
    token,
    minifiedBody,
    bodyHash,
    metadata,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Timestamp': String(requestTimestamp),
      'X-Nonce': requestNonce,
      'X-Sign': sign
    }
  }
}
